import { useAppStore } from "../store/useAppStore";
import { format, isToday, isPast, parseISO } from "date-fns";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Calendar as CalendarIcon, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function FollowUps() {
  const { leads, completeFollowUp } = useAppStore();
  const navigate = useNavigate();

  const allFollowUps = leads.flatMap((lead) =>
    lead.followUps.map((fu) => ({ ...fu, lead }))
  );
  type FollowUpWithLead = (typeof allFollowUps)[number];

  const pending = allFollowUps.filter((fu) => !fu.completed);
  const completed = allFollowUps
    .filter((fu) => fu.completed)
    .sort((a, b) => parseISO(b.dueDate).getTime() - parseISO(a.dueDate).getTime());

  const dueToday = pending.filter((fu) => isToday(parseISO(fu.dueDate)));
  const overdue = pending.filter((fu) => !isToday(parseISO(fu.dueDate)) && isPast(parseISO(fu.dueDate)));
  const upcoming = pending
    .filter((fu) => !isToday(parseISO(fu.dueDate)) && !isPast(parseISO(fu.dueDate)))
    .sort((a, b) => parseISO(a.dueDate).getTime() - parseISO(b.dueDate).getTime());

  const summaryCards = [
    {
      key: "today",
      label: "Due today",
      value: dueToday.length,
      helper: dueToday.length > 0 ? "Clear these first" : "Nothing due right now",
      tone: "amber",
      icon: CalendarIcon,
    },
    {
      key: "overdue",
      label: "Overdue",
      value: overdue.length,
      helper: overdue.length > 0 ? "Needs attention" : "You're caught up",
      tone: "rose",
      icon: CheckCircle2,
    },
    {
      key: "upcoming",
      label: "Upcoming",
      value: upcoming.length,
      helper: "Scheduled next",
      tone: "slate",
      icon: CalendarIcon,
    },
    {
      key: "completed",
      label: "Completed",
      value: completed.length,
      helper: "Closed out follow-ups",
      tone: "emerald",
      icon: CheckCircle2,
    },
  ] as const;

  const renderEmptyState = (title: string, body: string) => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-slate-500 min-h-[400px]">
      <CalendarIcon className="w-12 h-12 mb-4 opacity-50" />
      <h3 className="text-base font-semibold text-slate-800">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-slate-500">{body}</p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button onClick={() => navigate("/leads")} className="bg-blue-600 hover:bg-blue-700">
          Review leads
        </Button>
        <Button variant="outline" onClick={() => navigate("/leads")} className="border-slate-200 text-slate-700 hover:bg-slate-50">
          Open lead inbox
        </Button>
      </div>
    </div>
  );

  const renderTask = (fu: FollowUpWithLead, type: "today" | "overdue" | "upcoming" | "completed") => (
    <div
      key={fu.id}
      className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 gap-4"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-bold text-slate-800">{fu.lead.sellerName}</span>
          {type === "overdue" && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">Overdue</span>}
          {type === "today" && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Today</span>}
          {type === "upcoming" && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">{format(parseISO(fu.dueDate), "MMM d")}</span>}
          {type === "completed" && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Done</span>}
        </div>
        <p className="text-sm text-slate-500">
          {fu.lead.propertyAddress} • {fu.lead.askingPrice ? `$${fu.lead.askingPrice.toLocaleString()}` : "No Asking Price"}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="rounded-full bg-slate-50 px-2 py-1">
            Due {format(parseISO(fu.dueDate), "MMM d, yyyy")}
          </span>
          {fu.notes && (
            <span className="rounded-full bg-slate-50 px-2 py-1 italic text-slate-400">
              "{fu.notes}"
            </span>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/leads/${fu.lead.id}`)}
          className="w-full justify-between border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-semibold sm:w-auto"
        >
          <span>Open lead</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
        {type !== "completed" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => completeFollowUp(fu.lead.id, fu.id)}
            className="w-full justify-center gap-2 border-slate-200 text-slate-600 hover:border-green-200 hover:bg-green-50 hover:text-green-700 sm:w-auto"
          >
            <CheckCircle2 className="h-4 w-4" />
            Mark done
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-5 px-5 py-6 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Execution</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Follow-ups</span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Follow-Ups</h1>
            <p className="max-w-2xl text-sm leading-6 text-slate-500">Focus on what is due now, what is overdue, and what is queued next — all in one calmer view.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-500">Due today</p>
              <p className="mt-1 text-2xl font-semibold text-amber-700">{dueToday.length}</p>
            </div>
            <div className="rounded-2xl border border-rose-100 bg-rose-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-500">Overdue</p>
              <p className="mt-1 text-2xl font-semibold text-rose-700">{overdue.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Upcoming</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{upcoming.length}</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-500">Completed</p>
              <p className="mt-1 text-2xl font-semibold text-emerald-700">{completed.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const toneClasses = {
            amber: "bg-amber-100 text-amber-600",
            rose: "bg-rose-100 text-rose-600",
            slate: "bg-slate-100 text-slate-600",
            emerald: "bg-emerald-100 text-emerald-600",
          }[card.tone];

          return (
            <Card key={card.key} className="border-slate-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-4">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${toneClasses}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-slate-400">{card.label}</p>
                  <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                  <p className="text-xs text-slate-500">{card.helper}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="today" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-slate-100 border border-slate-200 shadow-sm p-1.5 rounded-xl mb-5 gap-1">
          <TabsTrigger value="today" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <span className="flex items-center gap-2">
              Due Today
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{dueToday.length}</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value="overdue" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-red-700 data-[state=active]:shadow-sm">
            <span className="flex items-center gap-2">
              Overdue
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">{overdue.length}</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-slate-800 data-[state=active]:shadow-sm">
            <span className="flex items-center gap-2">
              Upcoming
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">{upcoming.length}</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-green-700 data-[state=active]:shadow-sm">
            <span className="flex items-center gap-2">
              Completed
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">{completed.length}</span>
            </span>
          </TabsTrigger>
        </TabsList>

        <Card className="rounded-xl overflow-hidden border-slate-200 shadow-sm bg-white">
          <TabsContent value="today" className="m-0 border-0 outline-none">
            <div className="divide-y divide-slate-100 min-h-[400px]">
              {dueToday.length > 0 ? (
                dueToday.map((fu) => renderTask(fu, "today"))
              ) : (
                renderEmptyState(
                  "No follow-ups due today",
                  "You are caught up on today’s queue. Check overdue work or jump into upcoming tasks to stay ahead."
                )
              )}
            </div>
          </TabsContent>
          <TabsContent value="overdue" className="m-0 border-0 outline-none">
            <div className="divide-y divide-slate-100 min-h-[400px]">
              {overdue.length > 0 ? (
                overdue.map((fu) => renderTask(fu, "overdue"))
              ) : (
                renderEmptyState(
                  "No overdue follow-ups",
                  "Nothing is behind schedule right now. Use the upcoming tab to keep the pipeline moving."
                )
              )}
            </div>
          </TabsContent>
          <TabsContent value="upcoming" className="m-0 border-0 outline-none">
            <div className="divide-y divide-slate-100 min-h-[400px]">
              {upcoming.length > 0 ? (
                upcoming.map((fu) => renderTask(fu, "upcoming"))
              ) : (
                renderEmptyState(
                  "No upcoming follow-ups",
                  "There are no scheduled follow-ups yet. Open a lead and add the next touchpoint when you are ready."
                )
              )}
            </div>
          </TabsContent>
          <TabsContent value="completed" className="m-0 border-0 outline-none">
            <div className="divide-y divide-slate-100 min-h-[400px]">
              {completed.length > 0 ? (
                completed.map((fu) => renderTask(fu, "completed"))
              ) : (
                renderEmptyState(
                  "No completed follow-ups yet",
                  "Your completed list will appear here after you mark tasks done. Keep working from today or overdue to build momentum."
                )
              )}
            </div>
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  );
}
